import type {RefObject} from 'react';
import {useEffect, useRef, useState} from 'react';

const SHADOW_TOGGLE_THRESHOLD = 3;
const SCROLL_DEBOUNCE_DURATION_MS = 100;

export interface UseTopBottomScrollShadowObject {
  topShadow: boolean;
  bottomShadow: boolean;
}

export const useTopBottomScrollShadow = (scrollingContainer: RefObject<HTMLElement | null>, hasElementMounted?: boolean): UseTopBottomScrollShadowObject => {
  const [isPanelInitialized, setIsPanelInitialized] = useState(false);
  const [topShadow, setTopShadow] = useState(false);
  const [bottomShadow, setBottomShadow] = useState(false);
  const topShadowRef = useRef<boolean>(false);
  const bottomShadowRef = useRef<boolean>(false);

  const handleVerticalScroll = (): void => {
    const scrollTop = scrollingContainer.current?.scrollTop ?? 0;
    const clientHeight = scrollingContainer.current?.clientHeight ?? 0;
    const scrollHeight = scrollingContainer.current?.scrollHeight ?? 0;

    let newTopShadow = topShadowRef.current;
    let newBottomShadow = bottomShadowRef.current;
    if (scrollHeight > clientHeight) {
      if (scrollTop > SHADOW_TOGGLE_THRESHOLD) {
        newTopShadow = true;
      }
      if (scrollTop <= SHADOW_TOGGLE_THRESHOLD) {
        newTopShadow = false;
      }

      if (scrollTop + clientHeight + SHADOW_TOGGLE_THRESHOLD >= scrollHeight) {
        newBottomShadow = false;
      }
      if (scrollTop + clientHeight + SHADOW_TOGGLE_THRESHOLD < scrollHeight) {
        newBottomShadow = true;
      }
    } else {
      newBottomShadow = false;
      newTopShadow = false;
    }

    if (newTopShadow !== topShadowRef.current) {
      topShadowRef.current = newTopShadow;
      setTopShadow(newTopShadow);
    }

    if (newBottomShadow !== bottomShadowRef.current) {
      bottomShadowRef.current = newBottomShadow;
      setBottomShadow(newBottomShadow);
    }
  };

  const handleScroll = (): void => {
    if (scrollingContainer?.current) {
      handleVerticalScroll();
    }
  };

  useEffect(() => {
    const container = scrollingContainer.current;
    if (!container) {
      return;
    }

    handleScroll();

    const debouncedHandleScroll = window.PMW.debounce(handleScroll, SCROLL_DEBOUNCE_DURATION_MS);
    container.addEventListener('scroll', debouncedHandleScroll, {passive: true});

    const resizeObserver = new ResizeObserver(handleScroll);
    resizeObserver.observe(container);
    Array.from(container.children).forEach((child) => {
      resizeObserver.observe(child);
    });

    if (!isPanelInitialized) {
      setIsPanelInitialized(true);
    }

    return (): void => {
      container.removeEventListener('scroll', debouncedHandleScroll);
      resizeObserver.disconnect();
    };
  }, [scrollingContainer.current, scrollingContainer.current?.clientHeight, scrollingContainer.current?.scrollHeight, hasElementMounted]);

  return {
    topShadow,
    bottomShadow,
  };
};
